import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Header } from './components/layout/Header';
import { PageLoading } from './components/ui/LoadingSpinner';
import { useAuth } from './hooks/useAuth';
import { APP_BASE } from './config/app';

// ── Lazy-loaded pages ────────────────────────────────────────────────────────
const TournamentHome = lazy(() =>
  import('./pages/public/TournamentHome').then((m) => ({ default: m.TournamentHome })),
);
const OverallLeaderboard = lazy(() =>
  import('./pages/public/OverallLeaderboard').then((m) => ({ default: m.OverallLeaderboard })),
);
const RoundLeaderboard = lazy(() =>
  import('./pages/public/RoundLeaderboard').then((m) => ({ default: m.RoundLeaderboard })),
);
const RoundInfo = lazy(() =>
  import('./pages/public/RoundInfo').then((m) => ({ default: m.RoundInfo })),
);
const PlayerScorecard = lazy(() =>
  import('./pages/public/PlayerScorecard').then((m) => ({ default: m.PlayerScorecard })),
);
const GroupSetup = lazy(() =>
  import('./pages/scoring/GroupSetup').then((m) => ({ default: m.GroupSetup })),
);
const HoleScoring = lazy(() =>
  import('./pages/scoring/HoleScoring').then((m) => ({ default: m.HoleScoring })),
);
const SummaryScorecard = lazy(() =>
  import('./pages/scoring/SummaryScorecard').then((m) => ({ default: m.SummaryScorecard })),
);
const GroupProgress = lazy(() =>
  import('./pages/scoring/GroupProgress').then((m) => ({ default: m.GroupProgress })),
);
const FinaliseFlow = lazy(() =>
  import('./pages/scoring/FinaliseFlow').then((m) => ({ default: m.FinaliseFlow })),
);
const JoinViaLink = lazy(() =>
  import('./pages/scoring/JoinViaLink').then((m) => ({ default: m.JoinViaLink })),
);
const AdminLogin = lazy(() =>
  import('./pages/admin/AdminLogin').then((m) => ({ default: m.AdminLogin })),
);
const AdminDashboard = lazy(() =>
  import('./pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const AdminPlayers = lazy(() =>
  import('./pages/admin/AdminPlayers').then((m) => ({ default: m.AdminPlayers })),
);
const AdminCourses = lazy(() =>
  import('./pages/admin/AdminCourses').then((m) => ({ default: m.AdminCourses })),
);
const AdminRounds = lazy(() =>
  import('./pages/admin/AdminRounds').then((m) => ({ default: m.AdminRounds })),
);
const AdminChallenges = lazy(() =>
  import('./pages/admin/AdminChallenges').then((m) => ({ default: m.AdminChallenges })),
);
const AdminScoreAudit = lazy(() =>
  import('./pages/admin/AdminScoreAudit').then((m) => ({ default: m.AdminScoreAudit })),
);
const TournamentStats = lazy(() =>
  import('./pages/public/TournamentStats').then((m) => ({ default: m.TournamentStats })),
);

// ── Admin guard ──────────────────────────────────────────────────────────────
function AdminGuard({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!isAdmin) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

// ── App layout ────────────────────────────────────────────────────────────────
function AppLayout() {
  const { isAdmin } = useAuth();

  return (
    <div className="min-h-screen bg-off-white">
      <Header isAdmin={isAdmin} />
      <main className="pb-8">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<TournamentHome />} />
            <Route path="/leaderboard" element={<OverallLeaderboard />} />
            <Route path="/stats" element={<TournamentStats />} />
            <Route path="/rounds/:roundId" element={<RoundInfo />} />
            <Route path="/rounds/:roundId/leaderboard" element={<RoundLeaderboard />} />
            <Route path="/rounds/:roundId/players/:playerId/scorecard" element={<PlayerScorecard />} />

            {/* Scoring routes (require anonymous auth, validated by Firestore rules) */}
            <Route path="/rounds/:roundId/join/:joinCode" element={<JoinViaLink />} />
            <Route path="/rounds/:roundId/group" element={<GroupSetup />} />
            <Route path="/rounds/:roundId/group/:groupId" element={<HoleScoring />} />
            <Route path="/rounds/:roundId/group/:groupId/scorecard" element={<SummaryScorecard />} />
            <Route path="/rounds/:roundId/group/:groupId/progress" element={<GroupProgress />} />
            <Route path="/rounds/:roundId/group/:groupId/finalise" element={<FinaliseFlow />} />

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/players"
              element={
                <AdminGuard>
                  <AdminPlayers />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/courses"
              element={
                <AdminGuard>
                  <AdminCourses />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/rounds"
              element={
                <AdminGuard>
                  <AdminRounds />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/rounds/:roundId/challenges"
              element={
                <AdminGuard>
                  <AdminChallenges />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/rounds/:roundId/scores"
              element={
                <AdminGuard>
                  <AdminScoreAudit />
                </AdminGuard>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={APP_BASE}>
      <AppLayout />
    </BrowserRouter>
  );
}
